import { AKComponent } from '@theme/component';

/**
 * Accessible tabs component with keyboard navigation.
 *
 * Keyboard support:
 * - Arrow Left/Right: Navigate between tabs
 * - Home/End: Jump to first/last tab
 * - Enter/Space: Activate focused tab
 *
 * @extends {AKComponent}
 */
class TabsComponent extends AKComponent {
  connectedCallback() {
    super.connectedCallback();

    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.tabpanels = this.tabs.map(tab =>
      this.querySelector(`#${tab.getAttribute('aria-controls')}`)
    );

    this.initTabs();
    this.bindEvents();
  }

  initTabs() {
    // Set initial states
    this.tabs.forEach((tab, index) => {
      const isFirst = index === 0;
      tab.tabIndex = isFirst ? 0 : -1;
      tab.setAttribute('aria-selected', isFirst ? 'true' : 'false');

      // Ensure panels have correct initial visibility
      const panel = this.tabpanels[index];
      if (panel) {
        if (isFirst) {
          panel.classList.remove('is-hidden');
          panel.removeAttribute('hidden');
        } else {
          panel.classList.add('is-hidden');
          panel.setAttribute('hidden', '');
        }
      }
    });
  }

  bindEvents() {
    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => this.setSelectedTab(tab));
      tab.addEventListener('keydown', (event) => this.handleKeydown(event));
    });
  }

  setSelectedTab(selectedTab, shouldFocus = true) {
    this.tabs.forEach((tab, index) => {
      const isSelected = tab === selectedTab;
      const panel = this.tabpanels[index];

      // Update tab attributes
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.tabIndex = isSelected ? 0 : -1;

      // Update panel visibility
      if (panel) {
        if (isSelected) {
          panel.classList.remove('is-hidden');
          panel.removeAttribute('hidden');
          if (shouldFocus) {
            selectedTab.focus();
          }
        } else {
          panel.classList.add('is-hidden');
          panel.setAttribute('hidden', '');
        }
      }
    });
  }

  handleKeydown(event) {
    const target = event.currentTarget;
    const currentIndex = this.tabs.indexOf(target);
    let newTab = null;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        // Navigate to previous tab (with wrap-around)
        newTab = this.tabs[currentIndex - 1] || this.tabs[this.tabs.length - 1];
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        // Navigate to next tab (with wrap-around)
        newTab = this.tabs[currentIndex + 1] || this.tabs[0];
        break;
      case 'Home':
        // Jump to first tab
        newTab = this.tabs[0];
        break;
      case 'End':
        // Jump to last tab
        newTab = this.tabs[this.tabs.length - 1];
        break;
      case 'Enter':
      case ' ':
        // Activate current tab (if not already selected)
        event.preventDefault();
        this.setSelectedTab(target);
        return;
      default:
        return;
    }

    if (newTab) {
      event.preventDefault();
      this.setSelectedTab(newTab);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Cleanup if needed
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
